import React, { useRef, useState } from "react";
import { BusinessProvider } from "../../../../contexts/BusinessContext";
import SettingsHeader from "../SettingsHeader";
import { Heading, Subheading } from "../../../../design/heading";
import { Text } from "../../../../design/text";
import { Divider } from "../../../../design/divider";
import { Button } from "../../../../design/button";
import BusinessForm, {
  BusinessFormRef,
} from "../../../../components/Forms/BusinessForm";
import OrganizationProfileForm, {
  OrganizationProfileFormRef,
} from "../../../../components/Forms/OrganizationProfileForm";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../firebase";
import {
  BuildingOffice2Icon,
  BuildingLibraryIcon,
  CreditCardIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";

const BusinessPage: React.FC = () => {
  const formRef = useRef<BusinessFormRef>(null);
  const orgFormRef = useRef<OrganizationProfileFormRef>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingOrg, setIsSavingOrg] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const handleSubmit = async () => {
    if (formRef.current) {
      setIsSaving(true);
      await formRef.current.submitForm();
      setIsSaving(false);
    }
  };

  const handleOrgSubmit = async () => {
    if (orgFormRef.current) {
      setIsSavingOrg(true);
      await orgFormRef.current.submitForm();
      setIsSavingOrg(false);
    }
  };

  const onStartStripeIntegration = async () => {
    setIsConnecting(true);
    try {
      const fetchStripeConnectOnboardingLink = httpsCallable(
        functions,
        "fetchStripeConnectOnboardingLink",
      );
      const { data } = (await fetchStripeConnectOnboardingLink({
        refreshUrl: window.location.origin + "/onboarding",
        returnUrl: window.location.origin + "/onboarding",
      })) as { data: { url: string } };
      window.open(data.url, "_blank");
    } catch (error) {
      console.error("Error starting Stripe integration:", error);
      window.alert("Failed to start Stripe integration. Please try again later.");
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <BusinessProvider>
      <SettingsHeader />

      <div className="space-y-8">
        {/* Header */}
        <div>
          <Heading>Business Settings</Heading>
          <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
            Manage your business details, branding, and payment integrations.
          </Text>
        </div>

        {/* Business Details Section */}
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
              <BuildingOffice2Icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <Subheading>Business Information</Subheading>
          </div>

          <BusinessForm ref={formRef} isSaving={isSaving} />

          <div className="flex justify-end mt-6">
            <Button color="blue" onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>

        <Divider />

        {/* Organization Profile Section */}
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <BuildingLibraryIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <Subheading>Organization Profile</Subheading>
              <Text className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                Additional details about your organization type, services, and operations
              </Text>
            </div>
          </div>

          <OrganizationProfileForm ref={orgFormRef} isSaving={isSavingOrg} />

          <div className="flex justify-end mt-6">
            <Button color="blue" onClick={handleOrgSubmit} disabled={isSavingOrg}>
              {isSavingOrg ? "Saving..." : "Save Organization Profile"}
            </Button>
          </div>
        </div>

        <Divider />

        {/* Stripe Integration Section */}
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                <CreditCardIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <Subheading>Payment Integration</Subheading>
                <Text className="mt-1 text-zinc-600 dark:text-zinc-400">
                  Connect your business to Stripe to start accepting payments from clients.
                  Stripe Connect allows you to receive payouts directly to your bank account.
                </Text>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
            <div className="flex items-center gap-4">
              <img
                src="https://assets.technologyadvice.com/uploads/2024/03/stripe.png"
                alt="Stripe Logo"
                className="h-8"
              />
              <Text className="font-medium">Stripe Connect</Text>
            </div>
            <Button
              color="purple"
              onClick={onStartStripeIntegration}
              disabled={isConnecting}
            >
              <span className="flex items-center gap-2">
                {isConnecting ? "Connecting..." : "Connect Stripe"}
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              </span>
            </Button>
          </div>
        </div>

        {/* Info Section */}
        <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
          <Subheading className="mb-2">About Business Settings</Subheading>
          <Text className="text-sm text-zinc-600 dark:text-zinc-400">
            Your business information is displayed on invoices, reports, and client communications.
            Keep this information up to date to ensure professional correspondence with your clients.
          </Text>
        </div>
      </div>
    </BusinessProvider>
  );
};

export default BusinessPage;
